// /admin/v2/one-liners — True/False lines used inside Revision. VIEW ONLY (BRIEFS C11.10,
// SPEC §10): one-liners are made by the system from the notes and reach the database only
// through the content files (autoload); the admin cannot add, edit, switch off or load them
// here. The page lists them per chapter / unit with the line, True or False, the correct
// line (for false ones) and the notes point it comes from; filters (chapter, unit, True /
// False, words, switched-off lines); counts; pages of 200.
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { v2AdminChapters, v2OneLiners } from '../../../api/v2admin';
import V2Tabs, { chapterLabel } from './V2Tabs';

const PAGE = 200;

function Row({ o }) {
  return (
    <tr data-testid={`v2ol-row-${o.id}`} style={{ opacity: o.is_active ? 1 : 0.55 }}>
      <td style={{ verticalAlign: 'top', fontSize: 12, maxWidth: 260 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{[o.unit_number, o.unit_title].filter(Boolean).join(' ')}</div>
        <code>{o.point_code}</code>
        <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>{o.point_text}</div>
      </td>
      <td style={{ verticalAlign: 'top', minWidth: 280 }}>
        <div>{o.statement}</div>
        {!o.is_true && (
          <div data-testid="v2ol-correct" style={{ fontSize: 13, color: '#047857', marginTop: 4 }}>Correct line: {o.correct_statement}</div>
        )}
      </td>
      <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        <span className={`badge ${o.is_true ? 'badge-green' : 'badge-red'}`}>{o.is_true ? 'True' : 'False'}</span>
        {!o.is_active && <>{' '}<span className="badge badge-yellow">Off</span></>}
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Answered {o.seen} · right {o.correct}</div>
      </td>
    </tr>
  );
}

export default function AdminV2OneLiners() {
  const [params, setParams] = useSearchParams();
  const chapterId = params.get('chapter') || '';
  const unit = params.get('unit') || '';
  const kind = params.get('kind') || '';
  const [q, setQ] = useState('');
  const [showOff, setShowOff] = useState(true);
  const [offset, setOffset] = useState(0);
  useEffect(() => { setOffset(0); }, [chapterId, unit, kind, q, showOff]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key === 'chapter') next.delete('unit');
    setParams(next);
  };

  const chaptersQ = useQuery({ queryKey: ['v2a', 'chapters'], queryFn: v2AdminChapters });
  const listQ = useQuery({
    queryKey: ['v2a', 'one-liners', chapterId, unit, kind, q, showOff, offset],
    queryFn: () => v2OneLiners({
      chapter_id: chapterId || undefined, unit: unit || undefined, kind: kind || undefined,
      q: q || undefined, include_inactive: showOff, limit: PAGE, offset,
    }),
    placeholderData: keepPreviousData,
  });
  const chapters = (chaptersQ.data && (chaptersQ.data.chapters || chaptersQ.data)) || [];
  const data = listQ.data || { one_liners: [], total: 0, active: 0, true: 0, false: 0, units: [], chapters: [] };
  const counts = {};
  (data.chapters || []).forEach((c) => { counts[c.chapter_id] = c.count; });
  const from = data.total ? offset + 1 : 0;
  const to = Math.min(offset + PAGE, data.total);

  return (
    <div>
      <V2Tabs title="One-liners" intro="True/False lines shown inside Revision (4 lines, then 1 question). The system makes them from the notes — this page only shows them. Each line comes from one notes point; a false line shows its correct line." />
      <div data-testid="v2ol-viewonly" style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
        View only. To change the lines, the content file of the chapter is changed and loaded by the system.
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <select className="form-input" value={chapterId} data-testid="v2ol-chapter"
          onChange={(e) => setParam('chapter', e.target.value)} style={{ maxWidth: 420 }}>
          <option value="">All chapters</option>
          {Array.isArray(chapters) && chapters.map((c) => (
            <option key={c.chapter_id} value={c.chapter_id}>
              {c.class_level ? chapterLabel(c) : c.title}{counts[c.chapter_id] ? ` (${counts[c.chapter_id]})` : ''}
            </option>
          ))}
        </select>
        {chapterId && (
          <select className="form-input" value={unit} data-testid="v2ol-unit"
            onChange={(e) => setParam('unit', e.target.value)} style={{ maxWidth: 360 }}>
            <option value="">All units</option>
            {(data.units || []).map((u) => (
              <option key={u.code} value={u.code}>
                {u.kind === 'subtopic' ? '   ' : ''}{[u.number, u.title].filter(Boolean).join(' ')} ({u.count})
              </option>
            ))}
          </select>
        )}
        <select className="form-input" value={kind} data-testid="v2ol-kind"
          onChange={(e) => setParam('kind', e.target.value)} style={{ maxWidth: 160 }}>
          <option value="">True and False</option>
          <option value="true">True only</option>
          <option value="false">False only</option>
        </select>
        <input className="form-input" placeholder="Search lines, points or codes" value={q}
          data-testid="v2ol-search" onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 280 }} />
        <label style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} />
          Show switched-off lines
        </label>
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 8 }} data-testid="v2ol-count">
        {data.total} lines · {data.true} true · {data.false} false · {data.active} on
        {data.total > PAGE ? ` · showing ${from}–${to}` : ''}
      </div>
      {listQ.isLoading ? <div>Loading…</div> : listQ.isError ? (
        <div style={{ color: 'var(--danger)' }}>Could not load the lines. <button className="btn btn-ghost btn-sm" onClick={() => listQ.refetch()}>Try again</button></div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }}>
            <thead><tr><th>From notes</th><th>Line</th><th>True / False</th></tr></thead>
            <tbody>
              {data.one_liners.map((o) => <Row key={o.id} o={o} />)}
              {!data.one_liners.length && <tr><td colSpan={3} style={{ color: 'var(--text-muted)' }}>No lines here.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {data.total > PAGE && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-ghost" data-testid="v2ol-prev" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
          <button className="btn btn-ghost" data-testid="v2ol-next" disabled={to >= data.total}
            onClick={() => setOffset(offset + PAGE)}>Next</button>
        </div>
      )}
    </div>
  );
}
